import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  loadingText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      leftIcon,
      rightIcon,
      fullWidth = false,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseStyles = `
      inline-flex items-center justify-center font-medium rounded-xl
      transition-all duration-200 active:scale-95
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950
      disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
    `;

    const variantStyles: Record<ButtonVariant, string> = {
      primary: `
        bg-blue-500 text-white
        hover:bg-blue-600
        focus:ring-blue-500
        shadow-lg shadow-blue-500/20
      `,
      secondary: `
        bg-slate-800 text-white
        hover:bg-slate-700
        focus:ring-slate-500
        border border-white/10
      `,
      danger: `
        bg-red-500 text-white
        hover:bg-red-600
        focus:ring-red-500
        shadow-lg shadow-red-500/20
      `,
      ghost: `
        bg-transparent text-slate-400
        hover:text-white hover:bg-white/10
        focus:ring-slate-500
      `,
      outline: `
        bg-transparent text-slate-300
        border border-white/20
        hover:bg-white/5 hover:text-white hover:border-white/30
        focus:ring-slate-500
      `,
    };

    const sizeStyles: Record<ButtonSize, string> = {
      sm: 'text-xs px-3 py-1.5 gap-1.5',
      md: 'text-sm px-4 py-2.5 gap-2',
      lg: 'text-base px-6 py-3 gap-2.5',
    };

    const iconSizes: Record<ButtonSize, string> = {
      sm: 'w-3.5 h-3.5',
      md: 'w-4 h-4',
      lg: 'w-5 h-5',
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          fullWidth && 'w-full',
          className
        )}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className={cn('animate-spin', iconSizes[size])} />
            {loadingText || children}
          </>
        ) : (
          <>
            {leftIcon && <span className={iconSizes[size]}>{leftIcon}</span>}
            {children}
            {rightIcon && <span className={iconSizes[size]}>{rightIcon}</span>}
          </>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

// Icon Button variant
interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      children,
      variant = 'ghost',
      size = 'md',
      loading = false,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseStyles = `
      inline-flex items-center justify-center rounded-xl
      transition-all duration-200 active:scale-95
      focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-950
      disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100
    `;

    const variantStyles: Record<ButtonVariant, string> = {
      primary: 'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500',
      secondary: 'bg-slate-800 text-white hover:bg-slate-700 focus:ring-slate-500 border border-white/10',
      danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500',
      ghost: 'bg-transparent text-slate-400 hover:text-white hover:bg-white/10 focus:ring-slate-500',
      outline: 'bg-transparent text-slate-300 border border-white/20 hover:bg-white/5 hover:text-white focus:ring-slate-500',
    };

    const sizeStyles: Record<ButtonSize, string> = {
      sm: 'p-1.5',
      md: 'p-2',
      lg: 'p-3',
    };

    const iconSizes: Record<ButtonSize, string> = {
      sm: 'w-4 h-4',
      md: 'w-5 h-5',
      lg: 'w-6 h-6',
    };

    return (
      <button
        ref={ref}
        disabled={isDisabled}
        className={cn(
          baseStyles,
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      >
        {loading ? (
          <Loader2 className={cn('animate-spin', iconSizes[size])} />
        ) : (
          <span className={iconSizes[size]}>{children}</span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

// Button Group
interface ButtonGroupProps {
  children: React.ReactNode;
  className?: string;
  orientation?: 'horizontal' | 'vertical';
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  className,
  orientation = 'horizontal',
}) => {
  return (
    <div
      className={cn(
        'inline-flex',
        orientation === 'horizontal' ? 'flex-row' : 'flex-col',
        '[&>button]:rounded-none',
        orientation === 'horizontal'
          ? '[&>button:first-child]:rounded-l-xl [&>button:last-child]:rounded-r-xl'
          : '[&>button:first-child]:rounded-t-xl [&>button:last-child]:rounded-b-xl',
        '[&>button:not(:last-child)]:border-r-0',
        className
      )}
    >
      {children}
    </div>
  );
};

export default Button;
